import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Camera, X, RotateCcw, Check, Smartphone, RectangleVertical, ZoomIn, ZoomOut } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from '../context/LanguageContext';

import { API_URL } from '../config/api';

const API = API_URL;

export default function TransformationCamera({ onComplete, onCancel }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  
  const [mode, setMode] = useState(null); // 'selfie' or 'mirror'
  const [cameraReady, setCameraReady] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [zoom, setZoom] = useState(1.0);
  const [zoomCapabilities, setZoomCapabilities] = useState(null);
  const [needsRotation, setNeedsRotation] = useState(false);

  const startCamera = useCallback(async (facingMode) => {
    try {
      // Stop any existing stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      
      const constraints = {
        video: {
          facingMode: facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: false
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      
      // Check zoom capabilities
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        try {
          const capabilities = videoTrack.getCapabilities?.();
          if (capabilities?.zoom) {
            setZoomCapabilities({
              min: capabilities.zoom.min,
              max: Math.min(capabilities.zoom.max, 3.0),
              step: capabilities.zoom.step || 0.1
            });
            setZoom(1.0);
            await videoTrack.applyConstraints({ advanced: [{ zoom: 1.0 }] });
          } else {
            setZoomCapabilities(null);
          }
        } catch (e) {
          setZoomCapabilities(null);
        }
      }
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play();
          // Check if landscape - needs rotation to portrait
          const vw = videoRef.current.videoWidth;
          const vh = videoRef.current.videoHeight;
          setNeedsRotation(vw > vh);
          setCameraReady(true);
        };
      }
    } catch (err) {
      console.error('Camera error:', err);
      setError(t('camera.permissionError'));
    }
  }, [t]);

  // Apply zoom when it changes
  const applyZoom = useCallback(async (newZoom) => {
    if (streamRef.current) {
      const videoTrack = streamRef.current.getVideoTracks()[0];
      if (videoTrack) {
        try {
          await videoTrack.applyConstraints({ advanced: [{ zoom: newZoom }] });
          setZoom(newZoom);
        } catch (e) {
          console.log('Zoom not supported');
        }
      }
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setCameraReady(false);
  }, []);

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  const handleModeSelect = (selectedMode) => {
    setMode(selectedMode);
    const facingMode = selectedMode === 'selfie' ? 'user' : 'environment';
    startCamera(facingMode);
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    
    // If landscape, rotate to portrait
    if (needsRotation) {
      canvas.width = vh;  // Swap dimensions for portrait
      canvas.height = vw;
    } else {
      canvas.width = vw;
      canvas.height = vh;
    }
    
    const ctx = canvas.getContext('2d');
    ctx.save();
    
    if (needsRotation) {
      // Rotate 90 degrees clockwise for portrait
      ctx.translate(canvas.width, 0);
      ctx.rotate(Math.PI / 2);
      
      // Flip for selfie after rotation
      if (mode === 'selfie') {
        ctx.translate(0, canvas.width);
        ctx.scale(1, -1);
      }
    } else {
      // Just flip horizontally for selfie
      if (mode === 'selfie') {
        ctx.translate(canvas.width, 0);
        ctx.scale(-1, 1);
      }
    }
    
    ctx.drawImage(video, 0, 0, vw, vh);
    ctx.restore();
    
    const imageData = canvas.toDataURL('image/jpeg', 0.85);
    setCapturedImage(imageData);
    stopCamera();
  };

  const retakePhoto = () => {
    setCapturedImage(null);
    const facingMode = mode === 'selfie' ? 'user' : 'environment';
    startCamera(facingMode);
  };

  const generateTransformation = async () => {
    if (!capturedImage) return;
    
    setGenerating(true);
    setError('');
    
    try {
      const response = await axios.post(
        `${API}/api/transformation/generate`,
        {
          image_base64: capturedImage,
          mode: mode
        },
        { withCredentials: true }
      );
      
      if (response.data.success) {
        // Background processing started - close immediately
        onComplete(response.data.transformation);
      }
    } catch (err) {
      setError(err.response?.data?.detail || t('camera.generationError'));
      setGenerating(false);
    }
    // Don't set generating=false on success - modal closes immediately
  };

  // Mode selection screen
  if (!mode) {
    return createPortal(
      <div className="fixed inset-0 bg-black z-[300] flex flex-col">
        <div 
          className="flex items-center justify-between p-4"
          style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 8px)' }}
        >
          <button onClick={onCancel} className="p-2 text-white">
            <X size={24} />
          </button>
          <h1 className="text-white font-semibold">{t('camera.takePhoto')}</h1>
          <div className="w-10" />
        </div>
        
        <div className="flex-1 flex flex-col items-center justify-center px-6">
          <p className="text-white/70 text-center mb-8">
            {t('camera.chooseMode')}
          </p>
          
          <div className="grid grid-cols-2 gap-4 w-full max-w-sm">
            {/* Selfie Mode */}
            <button
              onClick={() => handleModeSelect('selfie')}
              className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 flex flex-col items-center gap-3 hover:bg-white/20 transition-colors"
            >
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center">
                <Smartphone size={32} className="text-white" />
              </div>
              <span className="text-white font-semibold">{t('camera.selfie')}</span>
              <span className="text-white/60 text-xs text-center">
                {t('camera.selfieDescription')}
              </span>
            </button>
            
            {/* Mirror Mode */}
            <button
              onClick={() => handleModeSelect('mirror')}
              className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 flex flex-col items-center gap-3 hover:bg-white/20 transition-colors"
            >
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-cyan-600 flex items-center justify-center">
                <RectangleVertical size={32} className="text-white" />
              </div>
              <span className="text-white font-semibold">{t('camera.mirror')}</span>
              <span className="text-white/60 text-xs text-center">
                {t('camera.mirrorDescription')}
              </span>
            </button>
          </div>
        </div>
        
        {/* Safe area spacer at bottom */}
        <div style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }} />
      </div>,
      document.body
    );
  }

  // Camera/Preview screen
  return createPortal(
    <div className="fixed inset-0 bg-black z-[300] flex flex-col">
      {/* Header */}
      <div 
        className="flex items-center justify-between p-4 relative z-10"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 8px)' }}
      >
        <button 
          onClick={() => {
            stopCamera();
            setCapturedImage(null);
            setMode(null);
          }} 
          className="p-2 text-white"
        >
          <X size={24} />
        </button>
        <h1 className="text-white font-semibold">
          {mode === 'selfie' ? t('camera.selfieMode') : t('camera.mirrorMode')}
        </h1>
        <div className="w-10" />
      </div>

      {/* Camera/Preview */}
      <div
        className="flex-1 relative overflow-hidden flex items-center justify-center bg-black"
        data-testid="transformation-camera-stage"
      >
        {!capturedImage ? (
          <>
            {/* Black loading layer — kept above the <video> until the
                first frame is decoded. This prevents Android WebView
                from briefly showing its default "play media" Play-icon
                placeholder while the MediaStream is being attached. */}
            {!cameraReady && (
              <div
                className="absolute inset-0 z-[1] bg-black flex items-center justify-center"
                data-testid="camera-loading-overlay"
              >
                <div className="flex flex-col items-center gap-3">
                  <div className="w-10 h-10 border-3 border-white/40 border-t-white rounded-full animate-spin" />
                  <p className="text-white/70 text-sm">{t('camera.starting') || 'Avvio fotocamera...'}</p>
                </div>
              </div>
            )}

            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              // 1x1 transparent GIF as poster — suppresses Android
              // WebView's built-in Play-icon placeholder that flashes
              // before the first camera frame is available.
              poster="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
              className={`${needsRotation ? 'h-full' : 'w-full h-full'} object-cover ${mode === 'selfie' ? 'scale-x-[-1]' : ''} bg-black transition-opacity duration-200`}
              style={{
                opacity: cameraReady ? 1 : 0,
                ...(needsRotation ? {
                  transform: `rotate(90deg) ${mode === 'selfie' ? 'scaleY(-1)' : ''}`,
                  width: '177vh',
                  maxWidth: 'none',
                } : {}),
              }}
            />
            
            {/* Zoom control */}
            {cameraReady && zoomCapabilities && (
              <div className="absolute bottom-36 left-4 right-4">
                <div className="bg-black/50 backdrop-blur-sm rounded-2xl p-4">
                  <div className="flex items-center gap-3">
                    <ZoomOut className="w-6 h-6 text-white/60" />
                    <input
                      type="range"
                      min={zoomCapabilities.min}
                      max={zoomCapabilities.max}
                      step={zoomCapabilities.step}
                      value={zoom}
                      onChange={(e) => applyZoom(parseFloat(e.target.value))}
                      className="flex-1 h-3 bg-white/20 rounded-full appearance-none cursor-pointer"
                      style={{
                        WebkitAppearance: 'none',
                        background: `linear-gradient(to right, white ${((zoom - zoomCapabilities.min) / (zoomCapabilities.max - zoomCapabilities.min)) * 100}%, rgba(255,255,255,0.2) ${((zoom - zoomCapabilities.min) / (zoomCapabilities.max - zoomCapabilities.min)) * 100}%)`
                      }}
                    />
                    <ZoomIn className="w-6 h-6 text-white/60" />
                    <span className="text-white text-lg font-bold w-14 text-right">
                      {zoom.toFixed(1)}x
                    </span>
                  </div>
                </div>
              </div>
            )}
            
            {/* Instructions — only after the camera feed is live */}
            {cameraReady && (
              <div className="absolute bottom-28 left-0 right-0 text-center">
                <p className="text-white/80 text-sm bg-black/50 px-4 py-2 rounded-full inline-block">
                  {mode === 'selfie' 
                    ? t('camera.selfieInstruction')
                    : t('camera.mirrorInstruction')
                  }
                </p>
              </div>
            )}
          </>
        ) : (
          <img 
            src={capturedImage} 
            alt="Captured" 
            className="w-full h-full object-cover"
          />
        )}
      </div>

      {/* Hidden canvas for capture */}
      <canvas ref={canvasRef} className="hidden" />

      {/* Error message */}
      {error && (
        <div className="absolute top-20 left-4 right-4 bg-red-500/90 text-white p-3 rounded-xl text-center text-sm">
          {error}
        </div>
      )}

      {/* Controls - with safe area */}
      <div 
        className="p-4 flex items-center justify-center gap-6 bg-black/80"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
      >
        {!capturedImage ? (
          <button
            onClick={capturePhoto}
            disabled={!cameraReady}
            className={`w-20 h-20 rounded-full border-4 border-white flex items-center justify-center transition-all ${
              cameraReady 
                ? 'bg-white/20 active:scale-95' 
                : 'bg-white/10 opacity-50'
            }`}
          >
            <div className="w-14 h-14 rounded-full bg-white" />
          </button>
        ) : (
          <>
            {/* Retake */}
            <button
              onClick={retakePhoto}
              disabled={generating}
              className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center"
            >
              <RotateCcw size={24} className="text-white" />
            </button>
            
            {/* Confirm */}
            <button
              onClick={generateTransformation}
              disabled={generating}
              className="w-20 h-20 rounded-full bg-gradient-to-r from-[#E8334A] to-[#F59E0B] flex items-center justify-center"
            >
              {generating ? (
                <div className="animate-spin w-8 h-8 border-3 border-white border-t-transparent rounded-full" />
              ) : (
                <Check size={32} className="text-white" />
              )}
            </button>
          </>
        )}
      </div>

      {/* Generating overlay */}
      {generating && (
        <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center z-20">
          <div className="animate-spin w-16 h-16 border-4 border-white border-t-transparent rounded-full mb-4" />
          <p className="text-white text-lg font-medium">{t('camera.generating')}</p>
          <p className="text-white/60 text-sm mt-2">{t('camera.generatingWait')}</p>
        </div>
      )}
    </div>,
    document.body
  );
}
